/**
 * Lineage API Client
 *
 * API endpoints for data lineage visualization and impact analysis.
 *
 * Every method here has a UI caller. Five methods that had none
 * (runImpactAnalysis, getSnapshot, getEntityHistory, compareSnapshots,
 * triggerSnapshot) were removed in #1249 along with their hooks -- three of
 * them called routes that never existed. Where a future affordance needs
 * them, GET /lineage/impact/{node_id}, GET /lineage/snapshots/{id},
 * GET /lineage/snapshots/compare and POST /lineage/snapshots/capture are the
 * routes that do exist.
 */

import { apiClient } from './client';
import type {
  LineageGraph,
  LineageOptions,
  SnapshotListResponse,
  TimeTravelRequest,
  TimeTravelResponse,
  SnapshotStats,
  LineageHistoryFilters,
  LineageSearchResult,
} from '@/features/lineage/types/lineage.types';

/**
 * Get lineage graph for a specific entity.
 *
 * `entityId` may be a node id or a dataset id; the route resolves either.
 */
export const getLineage = async (
  entityId: string,
  options: LineageOptions
): Promise<LineageGraph> => {
  const response = await apiClient.get<LineageGraph>(`/lineage/graph/${entityId}`, {
    params: {
      direction: options.direction,
      depth: options.depth,
      include_metadata: options.includeMetadata,
      include_inactive: options.includeInactive,
    },
  });
  return response.data;
};

/**
 * Search lineage entities.
 *
 * The route declares `query`, not `q` -- sending `q` 422'd every keystroke,
 * so search had never worked (#1249). It also answers with a
 * LineageSearchResponse object, so the array the page maps over is
 * `.results`, not the body, projected from the backend LineageNode shape
 * (`node_id`/`entity_type`) to the one the page reads (`id`/`type`).
 */
export const searchLineageEntities = async (
  query: string
): Promise<LineageSearchResult[]> => {
  const response = await apiClient.get<{ results: any[] }>('/lineage/search', {
    params: { query },
  });
  return (response.data.results ?? []).map((node) => ({
    id: node.node_id,
    name: node.name,
    type: node.entity_type,
    qualified_name: node.qualified_name,
    metadata: node.metadata,
  }));
};

/**
 * Get lineage for a specific dataset.
 *
 * Points at the same graph route as getLineage: the catalog's
 * /catalog/datasets/{id}/lineage answers a third shape
 * ({dataset_id, upstream, downstream}) under a third parameter spelling
 * (`max_depth`), and both UI modes want a graph (#1249).
 */
export const getDatasetLineage = async (
  datasetId: string,
  direction: 'upstream' | 'downstream' | 'both' = 'both',
  depth: number = 5
): Promise<LineageGraph> => {
  const response = await apiClient.get<LineageGraph>(`/lineage/graph/${datasetId}`, {
    params: { direction, depth },
  });
  return response.data;
};

// ============================================================================
// Lineage History API Functions
// ============================================================================

/**
 * Get list of lineage snapshots with filtering
 */
export const getSnapshots = async (
  filters?: LineageHistoryFilters
): Promise<SnapshotListResponse> => {
  const response = await apiClient.get<SnapshotListResponse>('/lineage/snapshots', {
    params: {
      snapshot_type: filters?.snapshot_type?.join(','),
      start_date: filters?.start_date,
      end_date: filters?.end_date,
      limit: filters?.limit || 50,
      offset: filters?.offset || 0,
    },
  });
  return response.data;
};

/**
 * Get snapshot statistics
 */
export const getSnapshotStats = async (): Promise<SnapshotStats> => {
  const response = await apiClient.get<SnapshotStats>('/lineage/snapshots/stats');
  return response.data;
};

/**
 * Time-travel to reconstruct lineage graph at a specific point in time
 */
export const timeTravel = async (
  request: TimeTravelRequest
): Promise<TimeTravelResponse> => {
  const response = await apiClient.post<TimeTravelResponse>('/lineage/time-travel', {
    timestamp: request.timestamp,
    entity_id: request.entity_id,
    depth: request.depth,
    direction: request.direction,
  });
  return response.data;
};

export const lineageApi = {
  getLineage,
  searchLineageEntities,
  getDatasetLineage,
  getSnapshots,
  getSnapshotStats,
  timeTravel,
};
